import { Injectable, OnDestroy, inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject, first, takeUntil } from 'rxjs';

export interface EdiNavigationState {
  idList: string[];
  currentId: string;
  nextId: string;
  previousId: string;
  isNavigationReady: boolean;
  isNextButtonActive: boolean;
  isPreviousButtonActive: boolean;
}

interface EdiNavigationContext {
  idList$: BehaviorSubject<string[]>;
  currentId$: BehaviorSubject<string>;
  state$: BehaviorSubject<EdiNavigationState>;
}

const EMPTY_STATE: EdiNavigationState = {
  idList: [],
  currentId: '',
  nextId: '',
  previousId: '',
  isNavigationReady: false,
  isNextButtonActive: false,
  isPreviousButtonActive: false
};

/**
 * Generic next/previous navigation between EDI message detail pages, scoped per list route
 * (e.g. '/admin/edi/orders'). List components register the ids of the currently filtered/sorted
 * list before navigating to a detail page; detail components then read/advance through that list.
 */
@Injectable({
  providedIn: 'root'
})
export class EdiNavigationService implements OnDestroy {
  protected router = inject(Router);

  private unsubscribe: Subject<void> = new Subject();
  private contexts = new Map<string, EdiNavigationContext>();

  public setNavigation(baseRoute: string, currentId: string, idList: string[]): void {
    if (!baseRoute || (!currentId && (!idList || idList.length === 0))) {
      return;
    }
    const context = this.getOrCreateContext(baseRoute);
    context.idList$.next(idList || []);
    context.currentId$.next(currentId);
  }

  public setNavigationCurrentId(baseRoute: string, currentId: string): void {
    if (!baseRoute || !currentId) {
      return;
    }
    this.getOrCreateContext(baseRoute).currentId$.next(currentId);
  }

  public getState$(baseRoute: string): Observable<EdiNavigationState> {
    return this.getOrCreateContext(baseRoute).state$;
  }

  public goToNext(baseRoute: string): void {
    const state = this.getOrCreateContext(baseRoute).state$.value;
    if (!state.isNavigationReady || !state.nextId) {
      return;
    }
    this.navigateToId(baseRoute, state.nextId);
  }

  public goToPrevious(baseRoute: string): void {
    const state = this.getOrCreateContext(baseRoute).state$.value;
    if (!state.isNavigationReady || !state.previousId) {
      return;
    }
    this.navigateToId(baseRoute, state.previousId);
  }

  private navigateToId(baseRoute: string, id: string): void {
    this.router.navigateByUrl(`${baseRoute}/${id}`, { replaceUrl: true });
    this.setNavigationCurrentId(baseRoute, id);
  }

  private getOrCreateContext(baseRoute: string): EdiNavigationContext {
    if (!this.contexts.has(baseRoute)) {
      const idList$ = new BehaviorSubject<string[]>([]);
      const currentId$ = new BehaviorSubject<string>('');
      const state$ = new BehaviorSubject<EdiNavigationState>(EMPTY_STATE);

      currentId$.pipe(takeUntil(this.unsubscribe)).subscribe(currentId => {
        idList$.pipe(first()).subscribe(idList => {
          if (currentId && idList.length > 0) {
            state$.next(this.computeState(currentId, idList));
          }
        });
      });

      this.contexts.set(baseRoute, { idList$, currentId$, state$ });
    }
    return this.contexts.get(baseRoute);
  }

  private computeState(currentId: string, idList: string[]): EdiNavigationState {
    const length = idList.length;
    const index = idList.findIndex(id => id === currentId);

    if (length <= 1 || index === -1) {
      return { ...EMPTY_STATE, idList, currentId };
    }

    return {
      idList,
      currentId,
      previousId: index > 0 ? idList[index - 1] : '',
      isPreviousButtonActive: index > 0,
      nextId: index < length - 1 ? idList[index + 1] : '',
      isNextButtonActive: index < length - 1,
      isNavigationReady: true
    };
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}
